import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleButton } from "@/components/GoogleButton";
import { roleHome } from "@/lib/format";

const Signup = () => {
  const { signup, isAuthenticated, user, initializing } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!initializing && isAuthenticated) {
    return <Navigate to={roleHome(user.role)} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const { ok, error, user: u } = await signup({
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      phone: form.phone || undefined,
      password: form.password,
    });
    setSubmitting(false);
    if (!ok) {
      setError(error);
      toast.error(error || "Signup failed");
      return;
    }
    toast.success(`Welcome, ${u.full_name.split(" ")[0]} — happy shopping!`);
    navigate("/account", { replace: true });
  };

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="signup-page">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-gold-light text-gold flex items-center justify-center">
            <UserPlus size={20} />
          </div>
          <h1 className="mt-4 font-display text-3xl font-semibold text-slate-900 tracking-tight">
            Create your account
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Save your favourites and get concierge follow-ups from our BDC team.
          </p>
        </div>

        <div className="mt-8 space-y-3">
          <GoogleButton label="Sign up with Google" returnRoute="/account" />
          <p className="text-[11px] text-slate-500 text-center leading-relaxed">
            Google sign-up is for customers only.
          </p>
        </div>

        <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
          <span className="flex-1 h-px bg-slate-200" />
          OR
          <span className="flex-1 h-px bg-slate-200" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form">
          <div>
            <Label htmlFor="signup-name" className="text-xs uppercase tracking-wider text-slate-500">
              Full name
            </Label>
            <Input
              id="signup-name"
              required
              value={form.full_name}
              onChange={update("full_name")}
              className="mt-1"
              data-testid="signup-name-input"
            />
          </div>
          <div>
            <Label htmlFor="signup-email" className="text-xs uppercase tracking-wider text-slate-500">
              Email
            </Label>
            <Input
              id="signup-email"
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              className="mt-1"
              data-testid="signup-email-input"
            />
          </div>
          <div>
            <Label htmlFor="signup-phone" className="text-xs uppercase tracking-wider text-slate-500">
              Phone (optional)
            </Label>
            <Input
              id="signup-phone"
              value={form.phone}
              onChange={update("phone")}
              className="mt-1"
              data-testid="signup-phone-input"
            />
          </div>
          <div>
            <Label htmlFor="signup-password" className="text-xs uppercase tracking-wider text-slate-500">
              Password (min 8 chars)
            </Label>
            <Input
              id="signup-password"
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={update("password")}
              className="mt-1"
              data-testid="signup-password-input"
            />
          </div>
          {error && (
            <p className="text-sm text-rose-600" data-testid="signup-error">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 bg-brand hover:bg-brand-hover text-white font-semibold"
            data-testid="signup-submit-button"
          >
            {submitting ? "Creating account…" : "Create account"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-600">
          Already registered?{" "}
          <Link to="/login" className="font-semibold text-brand hover:underline" data-testid="link-to-login">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
